import en from "./messages/en.json";
import es from "./messages/es.json";
import ptBR from "./messages/pt-BR.json";
import { DEFAULT_UI_LOCALE, type UiLocale } from "@lib/locale";

export type Messages = typeof en;

const catalogs: Record<UiLocale, Messages> = {
  en,
  es,
  "pt-BR": ptBR,
};

export type TranslationKey = string;

function getNestedValue(obj: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((current, segment) => {
    if (current && typeof current === "object" && segment in current) {
      return (current as Record<string, unknown>)[segment];
    }
    return undefined;
  }, obj);
}

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, key: string) => {
    const value = params[key];
    return value !== undefined ? String(value) : `{${key}}`;
  });
}

export function getTranslator(locale: UiLocale = DEFAULT_UI_LOCALE) {
  const messages = catalogs[locale] ?? catalogs[DEFAULT_UI_LOCALE];

  function t(key: TranslationKey, params?: Record<string, string | number>): string {
    const value = getNestedValue(messages, key);
    if (typeof value !== "string") {
      return key;
    }
    return interpolate(value, params);
  }

  return { t, locale };
}

export type Translator = ReturnType<typeof getTranslator>;

/**
 * `t` returns the key itself when no message exists for it. Callers that would
 * rather show something human than a dotted key compare the result against the
 * key they passed - a rule that belongs next to the `t` that implements it,
 * not restated at each call site where it can quietly drift.
 */
export function translateOr(
  t: (key: string, params?: Record<string, string | number>) => string,
  key: string,
  fallback: string,
  params?: Record<string, string | number>,
): string {
  const label = t(key, params);
  return label === key ? fallback : label;
}

export function formatLocaleDate(date: Date, locale: UiLocale = DEFAULT_UI_LOCALE): string {
  return date.toLocaleDateString(locale);
}
